import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { FileText, Grid2x2, LogOut, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';

export function Layout() {
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useIsMobile();

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    return (
        <div className="min-h-screen min-h-safe bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 font-sans flex flex-col">
            {/* Desktop Header - Hidden on Mobile */}
            {!isMobile && (
                <header className="sticky top-0 z-50 w-full border-b border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-950/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
                    <div className="container flex h-14 max-w-screen-2xl items-center justify-between px-4">
                        <div className="flex items-center gap-6">
                            <Link to="/" className="font-bold text-lg tracking-tight">Loom</Link>
                            <nav className="flex items-center gap-4 text-sm font-medium">
                                <Link
                                    to="/"
                                    className={`hover:text-zinc-900 dark:hover:text-zinc-50 ${location.pathname === '/'
                                        ? 'text-zinc-900 dark:text-zinc-50'
                                        : 'text-zinc-500 dark:text-zinc-400'
                                        }`}
                                    aria-label="Memos"
                                >
                                    <FileText className="h-5 w-5" />
                                </Link>
                                <Link
                                    to="/entities"
                                    className={`hover:text-zinc-900 dark:hover:text-zinc-50 ${location.pathname === '/entities'
                                        ? 'text-zinc-900 dark:text-zinc-50'
                                        : 'text-zinc-500 dark:text-zinc-400'
                                        }`}
                                    aria-label="Entities"
                                >
                                    <Grid2x2 className="h-5 w-5" />
                                </Link>
                                <Link
                                    to="/review_log"
                                    className={`hover:text-zinc-900 dark:hover:text-zinc-50 ${location.pathname === '/review_log'
                                        ? 'text-zinc-900 dark:text-zinc-50'
                                        : 'text-zinc-500 dark:text-zinc-400'
                                        }`}
                                    aria-label="Review Logs"
                                >
                                    <History className="h-5 w-5" />
                                </Link>
                            </nav>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleLogout}
                                className="text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-50"
                            >
                                Logout
                            </Button>
                        </div>
                    </div>
                </header>
            )}

            {/* Mobile Header - Only on Mobile */}
            {isMobile && (
                <header className="sticky top-0 z-50 w-full border-b border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-950/80 backdrop-blur pt-safe">
                    <div className="flex h-14 items-center justify-between px-4">
                        <div className="flex items-center gap-4">
                            <span className="font-bold text-lg tracking-tight">Loom</span>
                            <nav className="flex items-center gap-3 text-sm font-medium">
                                <Link
                                    to="/"
                                    className={`hover:text-zinc-900 dark:hover:text-zinc-50 ${location.pathname === '/'
                                        ? 'text-zinc-900 dark:text-zinc-50'
                                        : 'text-zinc-500 dark:text-zinc-400'
                                        }`}
                                    aria-label="Memos"
                                >
                                    <FileText className="h-5 w-5" />
                                </Link>
                                <Link
                                    to="/entities"
                                    className={`hover:text-zinc-900 dark:hover:text-zinc-50 ${location.pathname === '/entities'
                                        ? 'text-zinc-900 dark:text-zinc-50'
                                        : 'text-zinc-500 dark:text-zinc-400'
                                        }`}
                                    aria-label="Entities"
                                >
                                    <Grid2x2 className="h-5 w-5" />
                                </Link>
                                <Link
                                    to="/review_log"
                                    className={`hover:text-zinc-900 dark:hover:text-zinc-50 ${location.pathname === '/review_log'
                                        ? 'text-zinc-900 dark:text-zinc-50'
                                        : 'text-zinc-500 dark:text-zinc-400'
                                        }`}
                                    aria-label="Review Logs"
                                >
                                    <History className="h-5 w-5" />
                                </Link>
                            </nav>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleLogout}
                                className="text-zinc-500"
                            >
                                <LogOut className="h-5 w-5" />
                            </Button>
                        </div>
                    </div>
                </header>
            )}

            <main className="container max-w-screen-md mx-auto px-4 py-6 flex-1">
                <Outlet />
            </main>
        </div>
    );
}
